// « Comparer » : tableau variantes × critères (mêmes critères pour TRAME et pour le technicien, § 7.2), besoin couvert par pièce,
// classement expliqué ; puis les deux plans côte à côte sur ordinateur, ou une bascule entre les deux sur téléphone.
// Aucun montant sans le droit devis.couts : la ligne « coût » n'existe pas pour ces comptes.
import { useEffect, useMemo, useState } from 'react';
import { useSession } from '../../../lib/session.tsx';
import { Badge, Chargement, ChampChoix, EtatErreur } from '../../../components/ui.tsx';
import type { PlanVue } from '../modele.ts';
import type { Calque, Selection } from './CalquesVariante.tsx';
import { useComparaison, useDetails } from './donnees.ts';
import { PlanVariante, etiquetteVariante } from './Visuel.tsx';
import { couverturesParPiece, LIBELLE_TON, meilleurs, pointsLocalises, valeurCritere, type CalculVue, type PieceVue } from './logique.ts';

const CALQUES_COMPARAISON = new Set<Calque>(['pieces', 'unites', 'ue', 'reseau', 'derivations', 'points']);

function useLarge(seuil: number) {
  const requete = `(min-width: ${seuil}px)`;
  const [large, setLarge] = useState(() => typeof window !== 'undefined' && window.matchMedia(requete).matches);
  useEffect(() => {
    const m = window.matchMedia(requete);
    const f = () => setLarge(m.matches);
    m.addEventListener('change', f);
    return () => m.removeEventListener('change', f);
  }, [requete]);
  return large;
}

export function Comparaison({ dossierId, calcul, plans, pieces, ouvrirVariante }: {
  dossierId: string; calcul: CalculVue; plans: PlanVue[]; pieces: PieceVue[]; ouvrirVariante: (id: string) => void;
}) {
  const { peut } = useSession();
  const couts = peut('devis.couts');
  const comp = useComparaison(dossierId, calcul.runId);
  const ids = calcul.variantes.map((v) => v.id);
  const details = useDetails(dossierId, ids);
  const large = useLarge(1100);
  const trame = calcul.variantes.filter((v) => v.origine === 'trame');
  const tech = calcul.variantes.find((v) => v.origine === 'technicien');
  const [gauche, setGauche] = useState(trame[0]?.id ?? ids[0]);
  const [droite, setDroite] = useState(tech?.id ?? ids[1] ?? ids[0]);
  const [affiche, setAffiche] = useState<'gauche' | 'droite'>('gauche');
  const [planId, setPlanId] = useState(plans[0]?.id ?? '');
  const [selection, setSelection] = useState<Selection>(null);
  const piecesMap = useMemo(() => new Map(pieces.map((p) => [p.id, { planId: p.planId, polygone: p.polygone }])), [pieces]);
  const variantes = details.map((d) => d.data?.variante);
  const couvertures = useMemo(() => couverturesParPiece(variantes), [details.map((d) => d.dataUpdatedAt).join()]); // eslint-disable-line react-hooks/exhaustive-deps

  if (comp.isLoading) return <Chargement lignes={4} libelle="Chargement du comparatif…" />;
  if (comp.isError || !comp.data) return <EtatErreur erreur={comp.error} reessayer={() => comp.refetch()} />;
  const c = comp.data;
  const criteres = c.criteres.filter((k) => couts || k.cle !== 'cout');
  const options = calcul.variantes.map((v) => ({ valeur: v.id, libelle: `${v.rang}. ${v.libelle}${v.origine === 'technicien' ? ' (technicien)' : ''}` }));

  const plan = (id: string, cote: 'gauche' | 'droite') => {
    const i = ids.indexOf(id); const d = details[i]; const l = calcul.variantes[i];
    if (!l) return null;
    if (!d?.data) return d?.isError ? <EtatErreur erreur={d.error} /> : <Chargement lignes={3} />;
    const v = d.data.variante;
    return (
      <div className="pile" style={{ gap: 6 }} key={cote}>
        <p className="petit"><strong>{l.rang}. {l.libelle}</strong> · {l.criteres.bloquants} bloquant(s), {l.criteres.aVerifier} à vérifier</p>
        <PlanVariante v={v} plans={plans} pieces={pieces} actifs={CALQUES_COMPARAISON} compact points={pointsLocalises(v, piecesMap)} pointChoisi={null}
          choisirPoint={() => {}} selection={selection} choisir={setSelection} planId={planId} changerPlan={setPlanId} etiquette={etiquetteVariante(l)} />
      </div>
    );
  };

  return (
    <div className="pile-l">
      <section className="pile" style={{ gap: 8 }} aria-labelledby="prp-comp-titre">
        <h3 id="prp-comp-titre" className="cch-titre-tableau">Comparaison critère par critère</h3>
        <p className="tres-petit secondaire">Classement sans pondération : d’abord le moins de points bloquants, puis de points « à vérifier », puis la couverture des besoins, puis (si toutes les variantes ont un coût complet) le coût, puis le cuivre, les unités et les dérivations. La meilleure valeur de chaque ligne est marquée.</p>
        <div className="tableau-conteneur">
          <table className="tableau prp-comparatif">
            <thead>
              <tr>
                <th scope="col">Critère</th>
                {c.colonnes.map((col) => (
                  <th key={col.id} scope="col">
                    <button type="button" className="prp-entete-variante" onClick={() => ouvrirVariante(col.id)} title="Voir cette variante sur le plan">
                      <span className="prp-rang">{col.rang}</span><span>{col.libelle}</span>
                    </button>
                    <Badge ton={col.origine === 'technicien' ? 'info' : 'accent'} icone={false}>{col.origine === 'technicien' ? 'Technicien' : 'TRAME'}</Badge>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {criteres.map((k) => {
                const best = meilleurs(k.cle, k.valeurs);
                return (
                  <tr key={k.cle}>
                    <th scope="row">{k.libelle}</th>
                    {k.valeurs.map((x, i) => <td key={i} className={`num ${best.has(i) ? 'prp-meilleur' : ''}`}>{valeurCritere(k.cle, x, couts)}{best.has(i) && <span className="prp-sr"> (meilleure valeur)</span>}</td>)}
                  </tr>
                );
              })}
              {couvertures.length > 0 && <tr className="prp-sous-titre"><th scope="rowgroup" colSpan={c.colonnes.length + 1}>Besoin couvert par pièce (froid)</th></tr>}
              {couvertures.map((r) => (
                <tr key={r.pieceId}>
                  <th scope="row">{r.nom}</th>
                  {r.valeurs.map((e, i) => <td key={i} className={`num ${e?.ton === 'non_couvert' || e?.ton === 'sans_unite' ? 'prp-txt-danger' : ''}`}>
                    {!e ? '…' : e.couverture != null ? `${Math.round(e.couverture * 100)} %` : LIBELLE_TON[e.ton]}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!couts && <p className="tres-petit discret">Coût estimé : réservé aux comptes autorisés à voir les montants.</p>}
        <ol className="prp-classement">
          {c.classement.map((x) => <li key={x.varianteId} className="petit"><strong>{x.rang}. {x.libelle}</strong> — {x.explication}</li>)}
        </ol>
      </section>

      <section className="pile" style={{ gap: 8 }} aria-labelledby="prp-cote-titre">
        <h3 id="prp-cote-titre" className="cch-titre-tableau">Plans côte à côte</h3>
        <div className="prp-choix-comparaison">
          <ChampChoix libelle="À gauche" value={gauche} onChange={(e) => setGauche(e.target.value)} options={options} />
          <ChampChoix libelle="À droite" value={droite} onChange={(e) => setDroite(e.target.value)} options={options} />
        </div>
        {large ? <div className="prp-cote-a-cote">{plan(gauche, 'gauche')}{plan(droite, 'droite')}</div> : (
          <>
            <div className="segment" role="group" aria-label="Plan affiché">
              <button type="button" aria-pressed={affiche === 'gauche'} onClick={() => setAffiche('gauche')}>{calcul.variantes.find((v) => v.id === gauche)?.libelle ?? 'Gauche'}</button>
              <button type="button" aria-pressed={affiche === 'droite'} onClick={() => setAffiche('droite')}>{calcul.variantes.find((v) => v.id === droite)?.libelle ?? 'Droite'}</button>
            </div>
            {affiche === 'gauche' ? plan(gauche, 'gauche') : plan(droite, 'droite')}
          </>
        )}
      </section>
    </div>
  );
}
